import { useCallback, useEffect, useState } from 'react'
import { History, Undo2 } from 'lucide-react'
import { opcoApi } from '../../../services/api'
import { useToast } from '../../../context/ToastContext'
import { ConfirmDialog, Modal, PanelState, asList, errorMessage, secondaryBtn } from './shared'
import { canUndoRun, runStatusLabel } from './autoassign/helpers'
import { itemResultLabel, itemUndoLabel } from './workMoveHelpers'

function when(iso) {
  if (!iso) return ''
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

// Earlier "bring their existing work" runs into this OpCo, newest first. Undo puts back exactly what a run
// moved; anything changed since (moved again by hand, say) is left alone and shown as "changed since".
export default function WorkMoveRunsPanel({ opco, reloadKey, onUndone }) {
  const toast = useToast()
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [detail, setDetail] = useState(null)
  const [undoing, setUndoing] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await opcoApi.listWorkMoveRuns()
      setRuns(asList(res?.data, 'runs').filter((r) => r.opco_id === opco.id))
    } catch (err) {
      setError(errorMessage(err, 'Failed to load the runs'))
    } finally {
      setLoading(false)
    }
  }, [opco.id])

  useEffect(() => { load() }, [load, reloadKey])

  const open = async (run) => {
    try {
      const res = await opcoApi.getWorkMoveRun(run.uid)
      setDetail(res?.data?.run || null)
    } catch (err) {
      toast.error('Could not open the run', errorMessage(err))
    }
  }

  const confirmUndo = async () => {
    setBusy(true)
    try {
      const res = await opcoApi.undoWorkMoveRun(undoing.uid)
      const run = res?.data?.run || {}
      toast.success('Run undone', `${run.undone ?? 0} put back. Anything changed since was left alone.`)
      setUndoing(null)
      await load()
      await onUndone?.()
    } catch (err) {
      const code = err?.response?.data?.code
      toast.error(code === 'not_undoable' ? 'Already undone' : 'Could not undo', errorMessage(err))
      setUndoing(null)
      await load()
    } finally {
      setBusy(false)
    }
  }

  if (!loading && !error && runs.length === 0) return null

  return (
    <section aria-label="Moved work" className="border-t border-gray-100 dark:border-gray-800">
      <div className="flex items-center gap-2 px-5 py-3">
        <History className="h-4 w-4 text-gray-400" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Moved work</h3>
      </div>
      <PanelState loading={loading} error={error} onRetry={load} empty={false}>
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {runs.map((r) => (
            <li key={r.uid} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {r.user_email || r.user_id} <span className="text-xs text-gray-500">{when(r.created_at)} by {r.created_by_email || 'unknown'}</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {runStatusLabel(r.status)} - {r.moved} moved
                  {r.skipped ? `, ${r.skipped} skipped` : ''}{r.failed ? `, ${r.failed} failed` : ''}
                  {r.undone ? `, ${r.undone} put back` : ''}
                </p>
              </div>
              <button type="button" onClick={() => open(r)} className={secondaryBtn}>Details</button>
              {canUndoRun(r) && (
                <button type="button" onClick={() => setUndoing(r)} className={secondaryBtn}>
                  <Undo2 className="h-4 w-4" /> Undo
                </button>
              )}
            </li>
          ))}
        </ul>
      </PanelState>

      {detail && (
        <Modal title={`Moved work of ${when(detail.created_at)}`} onClose={() => setDetail(null)} wide labelledBy="opco-work-run-title">
          <p className="mb-3 text-xs text-gray-500">{runStatusLabel(detail.status)} - {detail.moved} moved, {detail.skipped} skipped, {detail.failed} failed, {detail.undone} put back</p>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr><th className="py-1 pr-3">Item</th><th className="py-1 pr-3">Result</th><th className="py-1">Undo</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {(detail.items || []).map((i) => (
                  <tr key={`${i.entity_type}:${i.entity_id}`}>
                    <td className="py-1.5 pr-3 capitalize">{i.entity_type} #{i.entity_id}</td>
                    <td className="py-1.5 pr-3">{itemResultLabel(i.result)}{i.error ? `: ${i.error}` : ''}</td>
                    <td className="py-1.5 text-xs text-gray-500">{itemUndoLabel(i.undo_result)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
      {undoing && (
        <ConfirmDialog
          title="Undo this run?"
          message={`The ${undoing.moved} items it moved go back to Unassigned. Anything that was changed since is left alone.`}
          confirmLabel="Undo"
          danger
          busy={busy}
          onConfirm={confirmUndo}
          onCancel={() => setUndoing(null)}
        />
      )}
    </section>
  )
}
